import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  signal,
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import {
  MAX_ALLOWED_PLAYBACK_RATE,
  PLAYBACK_RATES,
} from '@core/constants/video.constant';

/**
 * Replacement control bar for a `<video>` whose native controls have been switched off.
 *
 * The browser's own controls live in a closed shadow DOM, so their playback-speed menu cannot be
 * filtered (see sc-5745). When the company forbids faster playback we therefore hide the native
 * controls and render this bar instead, offering only the rates that are actually allowed.
 */
@Component({
  selector: 'app-video-controls',
  templateUrl: './video-controls.component.html',
  styleUrls: ['./video-controls.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatMenuModule,
    MatSliderModule,
    MatTooltipModule,
    TranslateModule,
  ],
})
export class VideoControlsComponent implements OnInit {
  /** The media element this bar drives. */
  readonly video = input.required<HTMLVideoElement>();

  /** Highest rate the viewer may select; rates above it are not offered. */
  readonly maxRate = input(MAX_ALLOWED_PLAYBACK_RATE);

  readonly playing = signal(false);
  readonly muted = signal(false);
  readonly currentTime = signal(0);
  readonly duration = signal(0);
  readonly rate = signal(1);
  readonly volume = signal(1);

  private readonly destroyRef = inject(DestroyRef);

  /** Only the rates at or below the cap — this is what makes the restriction real. */
  get availableRates(): number[] {
    return PLAYBACK_RATES.filter(rate => rate <= this.maxRate());
  }

  get progressPercent(): number {
    const total = this.duration();
    return total > 0 ? (this.currentTime() / total) * 100 : 0;
  }

  ngOnInit(): void {
    const video = this.video();

    const sync = (): void => {
      this.playing.set(!video.paused && !video.ended);
      this.muted.set(video.muted);
      this.currentTime.set(video.currentTime || 0);
      this.duration.set(Number.isFinite(video.duration) ? video.duration : 0);
      this.rate.set(video.playbackRate);
      this.volume.set(video.volume);
    };

    const events = [
      'play',
      'pause',
      'ended',
      'timeupdate',
      'durationchange',
      'loadedmetadata',
      'ratechange',
      'volumechange',
    ];
    events.forEach(name => video.addEventListener(name, sync));
    this.destroyRef.onDestroy(() => {
      events.forEach(name => video.removeEventListener(name, sync));
    });

    sync();
  }

  togglePlay(): void {
    const video = this.video();
    if (video.paused || video.ended) {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }

  toggleMute(): void {
    const video = this.video();
    video.muted = !video.muted;
  }

  seekTo(value: number): void {
    const video = this.video();
    const total = this.duration();
    if (total > 0) {
      video.currentTime = (value / 100) * total;
    }
  }

  setVolume(value: number): void {
    const video = this.video();
    video.volume = value;
    if (value > 0 && video.muted) {
      video.muted = false;
    }
  }

  setRate(rate: number): void {
    this.video().playbackRate = rate;
  }

  toggleFullscreen(): void {
    const video = this.video();
    const container = video.parentElement ?? video;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void container.requestFullscreen?.().catch(() => undefined);
    }
  }

  formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
